/**
 * BlockedUsersSection Component
 * Expandable section showing blocked users with unblock functionality.
 */

import { useAuth } from '@contexts/AuthContext';
import { useToast } from '@contexts/ToastContext';
import { useBlockedUsers } from '@hooks/useBlockedUsers';
import type { BlockedUser } from '@parlay-picker/shared/blockTypes';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Loader2, ShieldOff, UserX } from 'lucide-react';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';

// =============================================================================
// Component
// =============================================================================

export function BlockedUsersSection(): ReactElement {
  const { authFetch } = useAuth();
  const { showToast } = useToast();
  const { users, total, hasMore, loading, fetched, error, loadBlockedUsers, loadMore } =
    useBlockedUsers();

  const [isExpanded, setIsExpanded] = useState(false);
  const [unblockingUserId, setUnblockingUserId] = useState<string | null>(null);
  const [confirmUnblockUserId, setConfirmUnblockUserId] = useState<string | null>(null);

  // Load blocked users when section is expanded (only once)
  useEffect(() => {
    if (isExpanded && !fetched && !loading) {
      loadBlockedUsers();
    }
  }, [isExpanded, fetched, loading, loadBlockedUsers]);

  const handleUnblock = useCallback(
    async (user: BlockedUser): Promise<void> => {
      setUnblockingUserId(user.id);
      try {
        const response = await authFetch(`/api/blocks/${user.id}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          const data = await response.json().catch(() => null);
          throw new Error(data?.error || 'Failed to unblock user');
        }

        showToast(`${user.displayName} has been unblocked`, 'success');
        // Reload the list to reflect the change
        await loadBlockedUsers();
        setConfirmUnblockUserId(null);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Failed to unblock user', 'error');
      } finally {
        setUnblockingUserId(null);
      }
    },
    [authFetch, showToast, loadBlockedUsers],
  );

  const formatBlockedDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <section className="mb-6">
      <h3 className="text-lg font-medium text-theme-text mb-2">Blocked Users</h3>
      <p className="text-sm text-theme-text-tertiary mb-3">
        Blocked users cannot see your profile, send friend requests, or follow you
      </p>
      <div className="bg-theme-surface rounded-lg border border-theme-border overflow-hidden">
        {/* Expandable header */}
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-4 hover:bg-theme-surface-hover transition-colors"
        >
          <div className="flex items-center gap-3">
            <ShieldOff className="w-5 h-5 text-theme-text-tertiary" />
            <div className="text-left">
              <p className="text-sm font-medium text-theme-text">
                {total > 0 ? `${total} blocked user${total !== 1 ? 's' : ''}` : 'No blocked users'}
              </p>
              <p className="text-xs text-theme-text-tertiary">
                Click to {isExpanded ? 'collapse' : 'view and manage'}
              </p>
            </div>
          </div>
          <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <ChevronDown className="w-5 h-5 text-theme-text-tertiary" />
          </motion.div>
        </button>

        {/* Expandable content */}
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              key="blocked-users-list"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
            >
              <div className="border-t border-theme-border">
                {/* Loading state */}
                {loading && users.length === 0 && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 text-theme-text-tertiary animate-spin" />
                    <span className="ml-2 text-sm text-theme-text-tertiary">
                      Loading blocked users...
                    </span>
                  </div>
                )}

                {/* Error state */}
                {error && (
                  <div className="p-4">
                    <p className="text-sm text-theme-danger">{error}</p>
                    <button
                      type="button"
                      onClick={loadBlockedUsers}
                      className="mt-2 text-sm text-theme-primary hover:underline"
                    >
                      Try again
                    </button>
                  </div>
                )}

                {/* Empty state */}
                {!loading && !error && users.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-8 px-4">
                    <UserX className="w-8 h-8 text-theme-text-tertiary mb-2" />
                    <p className="text-sm text-theme-text-tertiary text-center">
                      You haven't blocked anyone
                    </p>
                  </div>
                )}

                {/* Blocked users list */}
                {users.length > 0 && (
                  <div className="divide-y divide-theme-border">
                    {users.map((user) => (
                      <div key={user.id} className="flex items-center justify-between px-4 py-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-theme-text truncate">
                            {user.displayName}
                          </p>
                          <p className="text-xs text-theme-text-tertiary">
                            Blocked {formatBlockedDate(user.blockedAt)}
                          </p>
                        </div>

                        {confirmUnblockUserId === user.id ? (
                          <div className="flex items-center gap-2 shrink-0 ml-3">
                            <button
                              type="button"
                              onClick={() => handleUnblock(user)}
                              disabled={unblockingUserId === user.id}
                              className="px-3 py-1.5 bg-theme-danger hover:bg-theme-danger/80 disabled:bg-theme-surface text-white text-xs font-medium rounded transition-colors"
                            >
                              {unblockingUserId === user.id ? 'Unblocking...' : 'Confirm'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmUnblockUserId(null)}
                              disabled={unblockingUserId === user.id}
                              className="px-3 py-1.5 bg-theme-bg hover:bg-theme-border text-theme-text-secondary text-xs font-medium rounded transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmUnblockUserId(user.id)}
                            className="shrink-0 ml-3 px-3 py-1.5 bg-theme-bg hover:bg-theme-border text-theme-text text-xs font-medium rounded transition-colors"
                          >
                            Unblock
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Load more */}
                {hasMore && (
                  <div className="px-4 py-3 border-t border-theme-border">
                    <button
                      type="button"
                      onClick={loadMore}
                      disabled={loading}
                      className="w-full px-4 py-2 bg-theme-bg hover:bg-theme-border disabled:bg-theme-surface text-theme-text text-sm font-medium rounded transition-colors"
                    >
                      {loading ? 'Loading...' : 'Load more'}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
